export type Orientation = 'portrait' | 'landscape';

/**
 * Signature-compatible with react-native `Image.getSize`. Injected so the
 * resolution logic stays pure and testable without a react-native mock.
 */
export type SizeGetter = (
  uri: string,
  onOk: (width: number, height: number) => void,
  onErr: () => void,
) => void;

/**
 * Orientation from the stored dimensions the API returns with each photo.
 * Returns undefined when either dimension is unknown (rows written before the
 * dimension backfill), in which case the caller falls back to a probe.
 */
export function orientationFromDimensions(
  width: number | null | undefined,
  height: number | null | undefined,
): Orientation | undefined {
  if (!width || !height || width <= 0 || height <= 0) return undefined;
  return height > width ? 'portrait' : 'landscape';
}

/**
 * Module-level cache of already-measured photo orientations, keyed by resolved
 * URI. The feed re-mounts PhotoGrid every time a card scrolls back into view;
 * without this cache each mount fired a fresh `getSize` network/decode probe.
 * With it, a URI is measured once per session and every later mount reads the
 * answer synchronously — no probe, no async state churn on the scroll hot path.
 */
const orientationCache = new Map<string, Orientation>();

/** Synchronous cache read. Returns undefined if the URI hasn't been measured. */
export function getCachedOrientation(uri: string): Orientation | undefined {
  return orientationCache.get(uri);
}

/** Test-only: reset the module cache between cases. */
export function clearOrientationCache(): void {
  orientationCache.clear();
}

/**
 * Resolve a photo's orientation, measuring only on a cache miss. A failed
 * measurement resolves to 'landscape' (the safe default layout) but is NOT
 * cached, so a later successful load can still record the true orientation.
 */
export function resolveOrientation(uri: string, getSize: SizeGetter): Promise<Orientation> {
  const cached = orientationCache.get(uri);
  if (cached) return Promise.resolve(cached);

  return new Promise<Orientation>((resolve) => {
    getSize(
      uri,
      (width, height) => {
        const orientation: Orientation = height > width ? 'portrait' : 'landscape';
        orientationCache.set(uri, orientation);
        resolve(orientation);
      },
      () => resolve('landscape'),
    );
  });
}
